import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiBlob } from "../api";
import {
  STAGE_PILLS, BOARD_STAGE_LABELS, COMPANY_STATUS_LABELS,
  dateShort, dateLong, dateTimeLong,
} from "./ui";
import {
  classifyTier, groupThreads, itemKey, emailMarks,
  cleanBody, cleanBodyFolded, summarize, dayRange, meetingRange,
  byDay, dayHeading, clockTime, gmailThreadUrl, threadIdOf, callActionItems, callSummaryPoints,
} from "./timelineModel.mjs";
import { Fact } from "./Facts";
import useEscapeLayer from "./useEscape";
import { formAnswers as readFormAnswers } from "./formAnswers.mjs";

// The unified contact timeline: activities, touchpoints and stage transitions
// merged by GET /contacts/:id/timeline, newest first, rendered as COMPACT ROWS
// ON ONE VERTICAL SPINE — icon, one-line summary, right-aligned date. No cards:
// a card per entry spent most of the page on borders instead of history.
//
// Three visual tiers (see classifyTier): milestones carry a filled black icon
// chip and a bold line, events are standard weight, chatter is gray and groups.
//
// Icon chips are monochrome with ONE exception, and it is the exception that
// earns colour: an email's arrow is blue going out and green coming in. Every
// row used to spell that out — "Outbound email · Re: Medable/Remotion" — which
// spent the first third of the line, and the eye's first stop, on the one fact
// the arrow already carried. The words are gone, the colour does the work, and
// the direction is still in the row's accessible name and its tooltip.
//
// Every row expands IN PLACE to its full content, per row, many at once, with a
// collapse-all control and Esc at the top. Two levels: a collapsed email-thread
// group opens into individual rows, and each of those then expands on its own.

const svg = (...paths) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {paths.map((d, i) => <path key={i} d={d} />)}
  </svg>
);

const ICON = {
  arrowRight: svg("M5 12h14", "M13 5l7 7-7 7"),
  arrowLeft: svg("M19 12H5", "M11 19l-7-7 7-7"),
  flag: svg("M4 22V4", "M4 4h13l-2 4 2 4H4"),
  phone: svg("M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0122 16.92z"),
  chat: svg("M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"),
  doc: svg("M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z", "M14 2v6h6M16 13H8M16 17H8M10 9H8"),
  note: svg("M12 20h9", "M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z"),
  calendar: svg("M16 2v4M8 2v4M3 10h18", "M3 4h18v18H3z"),
  globe: svg("M12 2a10 10 0 100 20 10 10 0 000-20z", "M2 12h20", "M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"),
  image: svg("M3 3h18v18H3z", "M8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3z", "M21 15l-5-5L5 21"),
  mail: svg("M2 4h20v16H2z", "M22 6l-10 7L2 6"),
  clip: svg("M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"),
};

const TYPE_ICON = {
  call: ICON.phone,
  meeting: ICON.calendar,
  note: ICON.note,
  task: ICON.note,
  form_submission: ICON.doc,
  whatsapp_message: ICON.chat,
  linkedin_message: ICON.chat,
  slack: ICON.chat,
};

const MESSAGE_LABEL = { whatsapp_message: "WhatsApp message", linkedin_message: "LinkedIn message" };
// Fallback headline for an activity saved without a title (an inline note is).
const TYPE_LABEL = { note: "Note", task: "Task", meeting: "Meeting", slack: "Slack message" };

// Evidence source → what the automation actually read. 'gmail' covers two
// different automations, so the move's target tells them apart: a contact only
// reaches SQL off the proposal/pricing email (automation rule 4).
const EVIDENCE_LABEL = { fireflies: "call", gmail_pandadoc: "PandaDoc", gmail: "email", calendar: "calendar", mcp: "chat" };
const REF_LINK_LABEL = { fireflies: "view transcript", gmail_pandadoc: "view email", gmail: "view thread", calendar: "view event" };

function evidenceLabel(it) {
  const src = it.change_evidence_source;
  if (!src) return null;
  if (src === "gmail") return it.to_value === "salesqualifiedlead" ? "proposal email" : "email";
  return EVIDENCE_LABEL[src] || src;
}

/**
 * How the move got made, from stage_transitions.source plus the pending change
 * that produced it. An automated move that waited in the inbox is stamped with
 * the day it was approved; only a change that was applied without review says
 * "auto".
 */
function provenance(it) {
  if (it.source === "automation") {
    // Not every automated move came from the inbox: an intake path that creates
    // the contact writes its entry transition with no proposal behind it.
    if (!it.change_status) return "automated";
    const when = it.change_status === "auto_applied" ? "auto" : `approved ${dateShort(it.change_resolved_at)}`;
    const label = evidenceLabel(it);
    return label ? `via ${label} · ${when}` : when;
  }
  if (it.source === "app") return "manual";
  if (it.source === "mcp") return "via chat";
  if (it.source === "hubspot_import") return "imported from HubSpot";
  return `via ${String(it.source || "").replace(/_/g, " ")}`;
}

const stageLabel = (v) => (v ? STAGE_PILLS[v]?.label || v.replace(/_/g, " ") : null);
const plain = (v) => (v ? String(v).replace(/_/g, " ") : null);

// The label vocabulary depends on which field moved. lifecycle_stage is the
// contact ladder; a company row that carries a contact_id uses the board's own
// stage/status names rather than pretending to be a lifecycle stage.
function transitionValue(field, value) {
  if (!value) return null;
  if (field === "lifecycle_stage") return stageLabel(value);
  if (field === "pipeline_stage") return (BOARD_STAGE_LABELS[value] || plain(value)).toLowerCase();
  if (field === "status") return COMPANY_STATUS_LABELS[value] || plain(value);
  if (field === "kind") return KIND_LABELS[value] || plain(value);
  return plain(value);
}

const FIELD_PREFIX = { lifecycle_stage: "", lead_status: "Lead status ", pipeline_stage: "Pipeline ", status: "Status " };

// A kind flip decides whether the company is in the reports at all (migration
// 044), so it reads as a sentence about the person, not a field name.
const KIND_LABELS = { lead: "Marked as a lead", associated: "Marked as an associated contact", junk: "Marked as junk" };

// A Fireflies transcript link, and a call's length, only exist on the activity
// when the ingest captured them — neither is rendered on a guess.
const transcriptUrl = (m) => {
  const url = m?.transcript_url || m?.fireflies_url || m?.url;
  return typeof url === "string" && /^https?:\/\//.test(url) ? url : null;
};
const durationMinutes = (m) => {
  const n = Number(m?.duration_minutes ?? m?.duration);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};
const httpLink = (ref) => (typeof ref === "string" && /^https?:\/\//.test(ref) ? ref : null);

/**
 * One timeline entry → its icon and its one-line summary. The line carries the
 * single most identifying fact for the type (a subject, a title, a duration, a
 * time range); everything else is in the expansion.
 */
function describe(it) {
  if (it.item_type === "stage_transition") {
    const to = transitionValue(it.subtype, it.to_value);
    const head = it.subtype === "kind"
      ? to
      : it.from_value || it.subtype !== "lifecycle_stage"
        ? `${FIELD_PREFIX[it.subtype] ?? ""}→ ${to}`
        : `Entered the funnel as ${to}`;
    return { icon: ICON.flag, line: `${head} · ${provenance(it)}` };
  }
  if (it.item_type === "touchpoint") {
    const meta = it.metadata || {};
    return {
      icon: ICON.globe,
      line: ["Touchpoint", plain(it.subtype) || "web", meta.utm_campaign || it.title || null]
        .filter(Boolean).join(" · "),
    };
  }

  const meta = it.metadata || {};

  if (it.subtype === "email") {
    const subject = String(it.title || "").trim();
    // THREE STATES, NOT TWO. This read `direction === "inbound"` and called
    // everything else ours, which is only true where a direction was written.
    // The HubSpot import never wrote one (scripts/backfill-email-direction.js),
    // so ~2,100 emails a prospect sent US wore a blue arrow and "Outbound email"
    // — Gabriel caught it on the Cynomi row that ended the deal. An envelope and
    // no colour is the honest render for a direction we do not hold, and it
    // follows the rule in docs/CONVENTIONS.md: show nothing, never the likely
    // candidate.
    if (meta.direction !== "inbound" && meta.direction !== "outbound") {
      return { icon: ICON.mail, line: subject || "(no subject)", dirLabel: "Email · direction not recorded" };
    }
    const inbound = meta.direction === "inbound";
    // Subject only. `dir` colours the arrow and `dirLabel` is what a screen
    // reader and a hover get, because direction must not live in colour alone.
    return {
      icon: inbound ? ICON.arrowLeft : ICON.arrowRight,
      line: subject || "(no subject)",
      dir: inbound ? "in" : "out",
      dirLabel: inbound ? "Inbound email" : "Outbound email",
    };
  }
  if (MESSAGE_LABEL[it.subtype]) {
    return {
      icon: ICON.chat,
      line: [MESSAGE_LABEL[it.subtype], plain(meta.direction), summarize(it.body, 70) || null]
        .filter(Boolean).join(" · "),
    };
  }
  if (it.subtype === "call") {
    const mins = durationMinutes(meta);
    return { icon: ICON.phone, line: `${it.title || "Call"}${mins ? ` · ${mins} min` : ""}` };
  }
  if (it.subtype === "meeting") {
    const range = meetingRange(it);
    return { icon: ICON.calendar, line: `${it.title || "Meeting"}${range ? ` · ${range}` : ""}` };
  }
  if (it.subtype === "form_submission") {
    return { icon: ICON.doc, line: `Form submission · ${it.title || "form"}` };
  }
  return {
    icon: TYPE_ICON[it.subtype] || ICON.note,
    line: [it.title || TYPE_LABEL[it.subtype] || plain(it.subtype) || "activity", summarize(it.body, 70) || null]
      .filter(Boolean).join(" · "),
  };
}

// ---------------------------------------------------------------- expansions

/**
 * What this row is holding, asked of THE ROW rather than of the calendar.
 *
 * The first version compared occurred_at against a hardcoded capture date, which was
 * wrong twice over: the constant said Aug 1 and capture actually shipped on the 5th,
 * and a date can only ever approximate what the data already states outright.
 *
 * body_kind is written by the watcher at capture time, so its ABSENCE is itself the
 * signal — a row with no body_kind predates capture entirely; a row that says
 * 'snippet' was captured but had nothing readable to store (a format:'metadata'
 * fetch). The data knows what it is.
 */
export function bodyNote(it) {
  if (!it || it.subtype !== "email") return null;
  const kind = (it.metadata || {}).body_kind;
  if (kind === "full") return null;
  if (!kind) return "snippet only — captured before full bodies were stored";
  return "snippet only — no readable body in this message";
}

const Body = ({ text, note }) => {
  const [showSig, setShowSig] = useState(false);
  const { body, signature } = cleanBodyFolded(text);
  return (
    <>
      {body
        ? <p className="crm-tlbody">{body}</p>
        : <p className="crm-tlbody crm-tlbody--empty">No body was stored with this entry.</p>}
      {/* Folded, never dropped: a wrong detection costs one click, deleting real
          content costs trust in every other thing on the page. */}
      {signature && (
        showSig
          ? <p className="crm-tlbody crm-tlbody__sig">{signature}</p>
          : <button type="button" className="crm-tlbody__sigtoggle" onClick={() => setShowSig(true)}>
              · signature
            </button>
      )}
      {note && <p className="crm-tlbody__note">{note}</p>}
    </>
  );
};

// The stored screenshot behind a DM entry. The attachment endpoint is
// auth-gated, so the bytes are fetched with the token and shown from an object
// URL; clicking opens that original in a new tab.
function AttachmentThumb({ id, filename }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    let live = true;
    apiBlob(`/attachments/${id}`)
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return (
    <button
      className="crm-tlthumb"
      disabled={!url}
      onClick={() => window.open(url, "_blank", "noopener")}
      title="Open the stored screenshot"
    >
      {url
        ? <img src={url} alt={filename || "Stored screenshot"} />
        : <span className="crm-tlthumb__ph">{ICON.image}</span>}
      <span>{filename || "screenshot"}</span>
    </button>
  );
}

const personLine = (p) =>
  [p && (p.name || p.displayName), p && p.email].filter(Boolean).join(" · ") || String(p || "");

function attendeeList(meta) {
  const list = Array.isArray(meta.attendees) ? meta.attendees : [];
  if (!list.length) return null;
  return <span className="crm-tlfact__stack">{list.map((a, i) => <span key={i}>{personLine(a)}</span>)}</span>;
}

function participantList(meta) {
  const list = Array.isArray(meta.participants) ? meta.participants : [];
  if (!list.length) return null;
  return <span className="crm-tlfact__stack">{list.map((p, i) => <span key={i}>{personLine(p)}</span>)}</span>;
}

function actionItems(meta) {
  const groups = callActionItems(meta.action_items);
  if (!groups.length) return null;
  return (
    <span className="crm-tlfact__stack">
      {groups.map((g, i) => (
        <React.Fragment key={i}>
          {g.owner && <b>{g.owner}</b>}
          {g.items.map((s, j) => <span key={j}>{s}</span>)}
        </React.Fragment>
      ))}
    </span>
  );
}

/** A Fireflies summary as the bullets it is, rather than its raw markdown. */
function CallSummary({ text }) {
  const points = callSummaryPoints(text);
  if (!points.length) return <Body text={text} />;
  return (
    <ul className="crm-tlbody crm-tlbody--list">
      {points.map((p, i) => (
        <li key={i}>{p.label && <b>{p.label}: </b>}{p.text}</li>
      ))}
    </ul>
  );
}

// The submitted form, answer by answer, through the same reader the rail card
// uses — so the row and the card can never disagree about what somebody said.
//
// It used to read `metadata.form` directly, which showed the normalised intake
// body (their name and email, which the row already names) and none of the
// structured answers, because those live in `form_context`. A HubSpot-imported
// submission has neither key and showed nothing at all.
function formAnswers(it) {
  const rows = readFormAnswers(it);
  if (!rows.length) return null;
  return (
    <span className="crm-tlfact__stack">
      {rows.map((r) => (
        <span key={r.key}>
          <b>{r.label}</b>: {r.value}
        </span>
      ))}
    </span>
  );
}

/** The evidence trail behind a milestone: the proposal that produced it. */
function MilestoneDetail({ it }) {
  const to = transitionValue(it.subtype, it.to_value);
  const from = transitionValue(it.subtype, it.from_value);
  const link = httpLink(it.change_evidence_ref);
  const label = evidenceLabel(it);
  return (
    <>
      <Fact label="Change">{from ? `${from} → ${to}` : `entered as ${to}`}</Fact>
      <Fact label="Field">{plain(it.subtype)}</Fact>
      <Fact label="Recorded">{dateTimeLong(it.occurred_at)}</Fact>
      {it.change_status ? (
        <>
          <Fact label="Evidence">{it.change_evidence}</Fact>
          <Fact label="Read from">{label}</Fact>
          <Fact label="Confidence">{plain(it.change_confidence)}</Fact>
          <Fact label={it.change_status === "auto_applied" ? "Auto-applied" : "Approved"}>
            {dateTimeLong(it.change_resolved_at)}
          </Fact>
          {/* resolved_by only exists from Aug 2026 on — a row without one says
              nothing rather than naming a likely candidate. */}
          <Fact label="By">
            {it.change_resolved_by === "automation" ? "automation, without review" : it.change_resolved_by}
          </Fact>
          <Fact label="Source">
            {link
              ? <a className="crm-row2__link" href={link} target="_blank" rel="noreferrer">{REF_LINK_LABEL[it.change_evidence_source] || "view source"}</a>
              : it.change_evidence_ref}
          </Fact>
        </>
      ) : (
        <Fact label="Evidence">
          {it.source === "automation"
            ? "Written by an intake path, with no proposal behind it."
            : "Set by hand on this page — no proposal behind it."}
        </Fact>
      )}
    </>
  );
}

/** Full content for one row, by type. */
function Detail({ it, contactLabel, contactId }) {
  if (it.item_type === "stage_transition") return <MilestoneDetail it={it} />;

  const meta = it.metadata || {};

  if (it.item_type === "touchpoint") {
    return (
      <>
        <Fact label="Channel">{plain(it.subtype)}</Fact>
        <Fact label="Landing page">{it.body}</Fact>
        <Fact label="Campaign">{meta.utm_campaign}</Fact>
        <Fact label="Source / medium">{[meta.utm_source, meta.utm_medium].filter(Boolean).join(" / ")}</Fact>
        <Fact label="Content / term">{[meta.utm_content, meta.utm_term].filter(Boolean).join(" / ")}</Fact>
        <Fact label="Referrer">{meta.referrer}</Fact>
        <Fact label="Occurred">{dateTimeLong(it.occurred_at)}</Fact>
      </>
    );
  }

  if (it.subtype === "email") {
    return (
      <>
        <Fact label="Subject">{it.title}</Fact>
        <Fact label="Direction">
          {meta.direction === "inbound"
            ? `inbound · ${contactLabel || "the contact"}`
            : meta.direction === "outbound"
              ? "outbound · Gabriel"
              : "not recorded"}
        </Fact>
        <Fact label="Sent">{dateTimeLong(it.occurred_at)}</Fact>
        {/* The id IS the link: it stays visible as the evidence it is, and
            clicking it opens the thread in Gmail. Absent for anything not
            ingested from Gmail, which has no thread to open. */}
        <Fact label="Thread">
          {gmailThreadUrl(threadIdOf(it))
            ? <a className="crm-row2__link" href={gmailThreadUrl(threadIdOf(it))} target="_blank" rel="noreferrer">
                {threadIdOf(it)} · open in Gmail ↗
              </a>
            : meta.thread_id}
        </Fact>
        {meta.bulk && <Fact label="Bulk mail">{`${meta.bulk} — logged, but no automation ran on it`}</Fact>}
        <Body text={it.body} note={bodyNote(it)} />
      </>
    );
  }

  if (it.subtype === "call") {
    const url = transcriptUrl(meta);
    const mins = durationMinutes(meta);
    return (
      <>
        <Fact label="Started">{dateTimeLong(it.occurred_at)}</Fact>
        <Fact label="Length">{mins ? `${mins} min` : null}</Fact>
        <Fact label="Participants">{participantList(meta)}</Fact>
        <Fact label="Action items">{actionItems(meta)}</Fact>
        <Fact label="Transcript">
          {url ? <a className="crm-row2__link" href={url} target="_blank" rel="noreferrer">view transcript</a> : null}
        </Fact>
        {/* The follow-up button lives in the calls panel above, beside the
            notes it is written from. */}
        <CallSummary text={it.body} />
      </>
    );
  }

  if (it.subtype === "meeting") {
    // With a range, the day plus the range says it all; without one, the start
    // timestamp is the only time we have.
    const range = meta.end_at || meta.all_day ? meetingRange(it) : null;
    return (
      <>
        <Fact label="When">{range ? `${dateLong(it.occurred_at)} · ${range}` : dateTimeLong(it.occurred_at)}</Fact>
        <Fact label="Attendees">{attendeeList(meta)}</Fact>
        <Fact label="Organizer">{meta.organizer}</Fact>
        <Fact label="Status">{plain(meta.status)}</Fact>
        <Body text={it.body} />
      </>
    );
  }

  if (it.subtype === "form_submission") {
    return (
      <>
        <Fact label="Form">{it.title}</Fact>
        <Fact label="Submitted">{dateTimeLong(it.occurred_at)}</Fact>
        <Fact label="Landing page">{meta.landing_page}</Fact>
        <Fact label="Answers">{formAnswers(it)}</Fact>
        <Body text={it.body} />
      </>
    );
  }

  return (
    <>
      <Fact label="Recorded">{dateTimeLong(it.occurred_at)}</Fact>
      {meta.direction && <Fact label="Direction">{plain(meta.direction)}</Fact>}
      <Fact label="Logged by">{plain(it.source)}</Fact>
      <Body text={it.body} />
      {it.attachment_id && <AttachmentThumb id={it.attachment_id} filename={it.attachment_filename} />}
    </>
  );
}

// ------------------------------------------------------------------- the rows

const CHEV = { open: "▾", closed: "▸" };

/**
 * `marks` are what the message IS beyond its subject — pricing, a nudge (see
 * emailMarks in timelineModel.mjs). `withDate` puts the day back on the stamp,
 * and defaults to the nested case: a nested row has no day header above it,
 * because it was opened out of a thread that spans days.
 */
export function Row({ it, contactLabel, contactId, open, onToggle, nested, marks, withDate = nested }) {
  const tier = classifyTier(it);
  const { icon, line, dir, dirLabel } = describe(it);
  return (
    <div className={`crm-tlrow crm-tlrow--${tier}${open ? " is-open" : ""}${nested ? " crm-tlrow--nested" : ""}`}>
      <button type="button" className="crm-tlrow__head" onClick={onToggle} aria-expanded={open}>
        <span className={`crm-tlrow__chip${dir ? ` crm-tlrow__chip--${dir}` : ""}`} title={dirLabel || undefined}>
          {icon}
          {dirLabel && <span className="crm-sr">{dirLabel}</span>}
        </span>
        <span className="crm-tlrow__line">{line}</span>
        {(marks || []).map((m) => (
          <span key={m.key} className={`crm-tlmark crm-tlmark--${m.key}`} title={m.title}>{m.label}</span>
        ))}
        {it.attachment_id && <span className="crm-tlrow__clip" title="Has a stored attachment">{ICON.clip}</span>}
        <span className="crm-tlrow__when crm-num">
          {withDate ? `${dateShort(it.occurred_at)} · ${clockTime(it.occurred_at)}` : clockTime(it.occurred_at)}
        </span>
        <span className="crm-tlrow__chev" aria-hidden="true">{open ? CHEV.open : CHEV.closed}</span>
      </button>
      {open && (
        <div className="crm-tlexp">
          <Detail it={it} contactLabel={contactLabel} contactId={contactId} />
        </div>
      )}
    </div>
  );
}

/**
 * A run of same-thread emails, collapsed to one row. The count chip opens the
 * GROUP into its individual rows; each of those then expands on its own chevron
 * — two levels, two distinct affordances.
 */
function GroupRow({ group, companyName, opened, onToggleGroup, isOpen, onToggleRow, contactLabel, marks }) {
  const label = group.subject || (companyName ? `${companyName}/Remotion thread` : "email thread");
  // A collapsed group must not hide the one thing you would open it for, so the
  // marks of its messages surface on the group row — each kind once, however
  // many messages inside carry it.
  const summary = [];
  for (const it of group.items) {
    for (const m of marks.get(itemKey(it)) || []) {
      if (!summary.some((s) => s.key === m.key)) summary.push(m);
    }
  }
  // Built from the group's own messages rather than passed down: every message
  // in a run shares the thread key that formed it.
  const gmail = gmailThreadUrl(threadIdOf(group.items[0]));
  return (
    <>
      <div className={`crm-tlrow crm-tlrow--chatter crm-tlrow--group${opened ? " is-open" : ""}`}>
        <button type="button" className="crm-tlrow__head" onClick={onToggleGroup} aria-expanded={opened}>
          <span className="crm-tlrow__chip">{ICON.mail}</span>
          <span className="crm-tlrow__count crm-num">
            {group.count} {opened ? CHEV.open : CHEV.closed}
          </span>
          <span className="crm-tlrow__line">
            {`emails · ${label} · ${dayRange(group.from, group.to)}`}
          </span>
          {summary.map((m) => (
            <span key={m.key} className={`crm-tlmark crm-tlmark--${m.key}`} title={m.title}>{m.label}</span>
          ))}
          <span className="crm-tlrow__when crm-num">{clockTime(group.to)}</span>
        </button>
        {gmail && (
          <a
            className="crm-tlrow__gmail"
            href={gmail}
            target="_blank"
            rel="noreferrer"
            title="Open this thread in Gmail"
          >
            Gmail ↗
          </a>
        )}
      </div>
      {opened && group.items.map((it) => (
        <Row
          key={itemKey(it)}
          it={it}
          nested
          contactLabel={contactLabel}
          marks={marks.get(itemKey(it))}
          open={isOpen(itemKey(it))}
          onToggle={() => onToggleRow(itemKey(it))}
        />
      ))}
    </>
  );
}

export default function Timeline({ items, contactLabel, companyName, contactId = null }) {
  const [openRows, setOpenRows] = useState(() => new Set());
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const rows = useMemo(() => groupThreads(items || []), [items]);
  const days = useMemo(() => byDay(rows), [rows]);
  // Computed over the WHOLE timeline, not per day section: a nudge is defined by
  // the message before it on its thread, which is usually in another day box.
  const marks = useMemo(() => emailMarks(items || []), [items]);
  const expandedCount = openRows.size + openGroups.size;

  const collapseAll = useCallback(() => {
    setOpenRows(new Set());
    setOpenGroups(new Set());
  }, []);

  // Esc collapses everything — the way back to the dense default without
  // hunting for the rows that are open. A layer (useEscape.js), so in a panel the
  // first Esc folds the rows and the next one closes the panel.
  useEscapeLayer(expandedCount > 0, collapseAll);

  const toggle = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleRow = toggle(setOpenRows);
  const toggleGroup = toggle(setOpenGroups);

  if (!rows.length) {
    return <div className="crm-empty">Nothing on the timeline yet. Emails, calls, DMs and stage moves land here.</div>;
  }

  const entries = (items || []).length;

  return (
    <>
      <div className="crm-tlbar">
        <span className="crm-tlbar__count crm-num">
          {entries} {entries === 1 ? "entry" : "entries"}
        </span>
        {expandedCount > 0 && (
          <button type="button" className="crm-linkact crm-tlbar__collapse" onClick={collapseAll}>
            collapse all ({expandedCount}) · esc
          </button>
        )}
      </div>

      <div className="crm-tl">
        {/* One header per day, times on the rows — see byDay. A date on every row
            repeated the same six characters down the whole page. */}
        {days.map((day) => (
          <div key={day.key} className="crm-tlday">
            <div className="crm-tlday__head">{dayHeading(day.at)}</div>
            {day.rows.map((row) =>
              row.kind === "group" ? (
            <GroupRow
              key={row.key}
              group={row}
              companyName={companyName}
              contactLabel={contactLabel}
              marks={marks}
              opened={openGroups.has(row.key)}
              onToggleGroup={() => toggleGroup(row.key)}
              isOpen={(k) => openRows.has(k)}
              onToggleRow={toggleRow}
            />
              ) : (
                <Row
                  key={row.key}
                  it={row.item}
                  contactLabel={contactLabel}
                  contactId={contactId}
                  marks={marks.get(row.key)}
                  open={openRows.has(row.key)}
                  onToggle={() => toggleRow(row.key)}
                />
              )
            )}
          </div>
        ))}
      </div>
    </>
  );
}
